import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/components/header.css";
import { ReactComponent as LogoSvg } from "../assets/icons/Logo.svg";
import Profile from "./Profile";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, ContactRound, LogOut, Tv } from "lucide-react";
import {
  ProfilesService,
  ProfileResponseDto,
} from "../services/profilesService";
import { toAbsoluteUrl } from "../config/api";

interface HeaderProps {
  isAuthenticated?: boolean;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isAuthenticated = false,
  onLogout,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileResponseDto | null>(null);

  // Sử dụng user từ context, chỉ fallback khi không có user
  const displayUser = user;

  const getInitials = (nameOrEmail?: string) => {
    const src = (nameOrEmail || "").trim();
    if (!src) return "U";
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    const single = parts[0];
    const base = single && single.includes("@") ? single.split("@")[0] : single;
    return (base?.[0] || "U").toUpperCase();
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const p = await ProfilesService.getMyProfile();
        if (!mounted) return;
        setProfile(p);
        if (p && p.avatar) {
          setAvatarUrl(toAbsoluteUrl(p.avatar));
        } else {
          setAvatarUrl(null);
        }
      } catch {
        setProfile(null);
        setAvatarUrl(null);
      }
    };
    if (displayUser) load();
    return () => {
      mounted = false;
    };
  }, [displayUser]);

  // Refresh profile data when side panel closes (in case profile was updated)
  useEffect(() => {
    if (!sideOpen && displayUser) {
      const refreshProfile = async () => {
        try {
          const p = await ProfilesService.getMyProfile();
          setProfile(p);
          if (p && p.avatar) {
            setAvatarUrl(toAbsoluteUrl(p.avatar));
          } else {
            setAvatarUrl(null);
          }
        } catch {
          // Ignore errors on refresh
        }
      };
      refreshProfile();
    }
  }, [sideOpen, displayUser]);

  return (
    <>
      <header>
        <div className="header-container">
          <div className="logo">
            <a href="/">
              <LogoSvg style={{ width: "160px", height: "60px" }} />
            </a>
          </div>
          <nav>
            <Link to="/">Trang chủ</Link>
            <Link to="/news">Bài viết</Link>
          </nav>
          {isAuthenticated && displayUser ? (
            <div className="header-user">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={profile?.fullName || displayUser.fullName}
                  className="header-avatar"
                />
              ) : (
                <div
                  className="header-avatar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#e0e7ff",
                    color: "#3730a3",
                    fontWeight: 600,
                    fontSize: 14,
                    userSelect: "none",
                  }}
                  title={
                    profile?.fullName ||
                    displayUser.fullName ||
                    displayUser.email
                  }
                >
                  {getInitials(
                    profile?.fullName ||
                      displayUser.fullName ||
                      displayUser.email
                  )}
                </div>
              )}
              <div className="header-info">
                <span className="header-name">
                  {profile?.fullName || displayUser.fullName}
                </span>
                <span className="header-role">
                  {displayUser.role === "admin"
                    ? "Quản trị viên"
                    : displayUser.role === "moderator"
                    ? "Kiểm duyệt viên"
                    : displayUser.role === "premium"
                    ? "Thành viên Premium"
                    : displayUser.role === "tool_user"
                    ? "Tool User"
                    : displayUser.role === "member"
                    ? "Thành viên"
                    : displayUser.role === "guest"
                    ? "Khách"
                    : displayUser.role}
                </span>
              </div>
              <button
                className="header-logout"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <ChevronDown size={16} />
              </button>
              {/* Dropdown menu */}
              {menuOpen && (
                <div className="dropdown-menu">
                  <div
                    className="menu-item"
                    onClick={() => {
                      setSideOpen(true);
                      setMenuOpen(false);
                    }}
                  >
                    <ContactRound size={16} /> Hồ sơ cá nhân
                  </div>
                  <div
                    className="menu-item"
                    onClick={async () => {
                      // Đảm bảo thông tin đăng nhập được lưu trước khi mở tab mới
                      const authData = localStorage.getItem("auth");
                      if (authData) {
                        // Mở tab mới với URL đầy đủ để đảm bảo load đúng domain
                        const newWindow = window.open(
                          window.location.origin + "/dashboard",
                          "_blank"
                        );

                        // Đảm bảo tab mới có thời gian load và nhận auth data
                        if (newWindow) {
                          newWindow.focus();

                          // Gửi thông tin auth đến tab mới sau khi nó load
                          const sendAuthData = () => {
                            try {
                              newWindow.postMessage(
                                {
                                  type: "AUTH_SYNC",
                                  authData: authData,
                                },
                                window.location.origin
                              );
                            } catch (error) {}
                          };

                          // Thử gửi ngay lập tức
                          sendAuthData();

                          // Thử gửi lại sau 500ms để đảm bảo tab mới đã load
                          setTimeout(sendAuthData, 500);

                          // Thử gửi lại sau 1s để đảm bảo tab mới đã load hoàn toàn
                          setTimeout(sendAuthData, 1000);
                        }
                      } else {
                        // Nếu không có auth data, chuyển hướng trong cùng tab
                        navigate("/dashboard");
                      }
                      setMenuOpen(false);
                    }}
                  >
                    <Tv size={16} /> Kênh Đăng Bài
                  </div>
                  <div
                    className="menu-item"
                    onClick={() => {
                      if (onLogout) onLogout();
                      setMenuOpen(false);
                    }}
                  >
                    <LogOut size={16} /> Đăng xuất
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/dang-nhap" className="login-button">
              Đăng nhập
            </Link>
          )}
        </div>
      </header>

      {/* Overlay khi side panel mở */}
      {sideOpen && (
        <div className="overlay" onClick={() => setSideOpen(false)} />
      )}

      {/* Side panel Profile */}
      {sideOpen && <Profile onClose={() => setSideOpen(false)} />}
    </>
  );
};

export default Header;
